<template>
  <teleport to="body">
    <transition name="ftour">
      <div
        v-if="active && current"
        class="ftour"
        role="dialog"
        aria-modal="true"
        :aria-label="`${pageTitle} 화면 안내`"
      >
        <button
          type="button"
          class="ftour__catch"
          :class="{ 'is-solid': !hole.ready }"
          aria-label="안내 닫기"
          @click="stopTour"
        />

        <div
          v-if="hole.ready"
          class="ftour__hole"
          :style="{
            top: `${hole.top}px`,
            left: `${hole.left}px`,
            width: `${hole.width}px`,
            height: `${hole.height}px`,
          }"
        />

        <div
          class="ftour__card"
          :class="{ 'is-center': !hole.ready }"
          :style="cardStyle"
        >
          <p class="ftour__step">{{ stepIndex + 1 }} / {{ total }}</p>
          <h3>{{ current.name }}</h3>
          <p class="ftour__desc">{{ current.description }}</p>

          <div class="ftour__actions">
            <button type="button" class="ftour__ghost" @click="stopTour">건너뛰기</button>
            <div class="ftour__nav">
              <button type="button" class="ftour__btn" :disabled="isFirst" @click="prevStep">
                이전
              </button>
              <button type="button" class="ftour__btn ftour__btn--primary" @click="nextStep">
                {{ isLast ? '완료' : '다음' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useFeatureTour } from '@/composables/useFeatureTour'

const route = useRoute()
const {
  active,
  current,
  stepIndex,
  total,
  isFirst,
  isLast,
  pageTitle,
  hole,
  nextStep,
  prevStep,
  stopTour,
  refreshHole,
} = useFeatureTour()

const cardStyle = computed(() => {
  if (!hole.value.ready || typeof window === 'undefined') return {}

  const gap = 14
  const cardW = Math.min(360, window.innerWidth - 24)
  const approxH = 210
  const spaceBelow = window.innerHeight - (hole.value.top + hole.value.height)
  const placeBelow = spaceBelow > approxH + gap || hole.value.top < approxH + gap

  let top = placeBelow
    ? hole.value.top + hole.value.height + gap
    : Math.max(12, hole.value.top - approxH - gap)
  top = Math.max(12, Math.min(top, window.innerHeight - approxH - 12))

  let left = hole.value.left + hole.value.width / 2 - cardW / 2
  left = Math.max(12, Math.min(left, window.innerWidth - cardW - 12))

  return {
    top: `${top}px`,
    left: `${left}px`,
    width: `${cardW}px`,
  }
})

function onKey(event: KeyboardEvent) {
  if (!active.value) return
  if (event.key === 'Escape') stopTour()
  if (event.key === 'ArrowRight' || event.key === 'Enter') nextStep()
  if (event.key === 'ArrowLeft') prevStep()
}

/*
 * 쿼리만 바뀌는 경우(탭·필터)에는 안내를 유지한다.
 * 다른 화면(name)으로 갈 때만 닫되, 직후 검색 이동으로 새 안내를 띄울 수 있게
 * 이전 name 이 있을 때만 동작한다.
 */
watch(
  () => route.name,
  (name, prev) => {
    if (!prev || name === prev) return
    if (active.value) stopTour()
  },
)

onMounted(() => {
  window.addEventListener('resize', refreshHole)
  window.addEventListener('scroll', refreshHole, true)
  window.addEventListener('keydown', onKey)
})

onUnmounted(() => {
  window.removeEventListener('resize', refreshHole)
  window.removeEventListener('scroll', refreshHole, true)
  window.removeEventListener('keydown', onKey)
  if (active.value) stopTour()
})
</script>

<style lang="scss" src="./FeatureTourOverlay.scss"></style>
